import { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, TextField } from "@mui/material";
import { onboarding } from '../../services/api';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';

type TemplatePart =
  | { type: 'text'; value: string }
  | { type: 'slot'; id: string; label: string; placeholder: string; value: string };

const BUSINESS_DESCRIPTION_TEMPLATE_PARTS: TemplatePart[] = [
  { type: 'text', value: 'ما در ' },
  {
    type: 'slot',
    id: 'field',
    label: 'حوزه فعالیت',
    placeholder: 'مثلاً فروش پوشاک، آموزش، مهاجرت، املاک',
    value: '',
  },
  { type: 'text', value: ' با هدف ' },
  {
    type: 'slot',
    id: 'goal',
    label: 'هدف کسب‌وکار',
    placeholder: 'مثلاً ساده‌تر کردن خرید برای مشتریان',
    value: '',
  },
  { type: 'text', value: ' فعالیت خود را آغاز کرده‌ایم و در زمینه‌ی ' },
  {
    type: 'slot',
    id: 'services',
    label: 'خدمات / محصولات',
    placeholder: 'مثلاً فروش لباس زنانه و اکسسوری',
    value: '',
  },
  { type: 'text', value: ' خدمات ارائه می‌دهیم. تمرکز اصلی ما بر ' },
  {
    type: 'slot',
    id: 'focus',
    label: 'تمرکز اصلی',
    placeholder: 'مثلاً کیفیت، قیمت مناسب، مشاوره تخصصی',
    value: '',
  },
  { type: 'text', value: ' است و تلاش می‌کنیم با تکیه بر ' },
  {
    type: 'slot',
    id: 'strength',
    label: 'مزیت یا توانمندی',
    placeholder: 'مثلاً تجربه تیم، تنوع محصول، پشتیبانی سریع',
    value: '',
  },
  {
    type: 'text',
    value:
      '، تجربه‌ای باکیفیت و قابل اعتماد برای مشتریان خود ایجاد کنیم.\nآنچه ما را متمایز می‌کند ',
  },
  {
    type: 'slot',
    id: 'difference',
    label: 'وجه تمایز',
    placeholder: 'مثلاً ارسال سریع، مشاوره دقیق، قیمت رقابتی',
    value: '',
  },
  { type: 'text', value: ' است و همواره به اصولی مانند ' },
  {
    type: 'slot',
    id: 'values',
    label: 'ارزش‌ها',
    placeholder: 'مثلاً صداقت، احترام، مسئولیت‌پذیری',
    value: '',
  },
  { type: 'text', value: ' پایبند هستیم. هدف ما ' },
  {
    type: 'slot',
    id: 'mission',
    label: 'هدف نهایی',
    placeholder: 'مثلاً افزایش رضایت مشتریان',
    value: '',
  },
  { type: 'text', value: ' و ایجاد ارزشی ماندگار برای ' },
  {
    type: 'slot',
    id: 'audience',
    label: 'مخاطبان / مشتریان',
    placeholder: 'مثلاً خانواده‌ها، فروشگاه‌ها، مدیران کسب‌وکار',
    value: '',
  },
  { type: 'text', value: ' می‌باشد.' },
];

function deepCloneParts(parts: TemplatePart[]): TemplatePart[] {
  return parts.map((p) => (p.type === 'text' ? { ...p } : { ...p }));
}

function BusinessDescriptionTemplateEditor(props: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  const { value, onChange, disabled, error, helperText } = props;
  const [parts, setParts] = useState<TemplatePart[]>(() => deepCloneParts(BUSINESS_DESCRIPTION_TEMPLATE_PARTS));
  const [activeSlotId, setActiveSlotId] = useState<string | null>(null);
  const [isTemplateMode, setIsTemplateMode] = useState(true);

  const finalText = useMemo(() => {
    return parts
      .map((part) => {
        if (part.type === 'text') return part.value;
        return part.value?.trim() || '...';
      })
      .join('');
  }, [parts]);

  const requiredSlotIds = useMemo(
    () => ['field', 'goal', 'services', 'focus', 'strength', 'difference', 'values', 'mission', 'audience'],
    []
  );

  const filledCount = useMemo(() => {
    let count = 0;
    for (const part of parts) {
      if (part.type === 'slot' && part.value.trim()) count += 1;
    }
    return count;
  }, [parts]);

  const requiredMissingCount = useMemo(() => {
    const filled = new Set<string>();
    for (const part of parts) {
      if (part.type === 'slot' && part.value.trim()) filled.add(part.id);
    }
    return requiredSlotIds.filter((id) => !filled.has(id)).length;
  }, [parts, requiredSlotIds]);

  useEffect(() => {
    // If parent has a saved/loaded value that isn't the default template,
    // keep it as manual mode to avoid losing data.
    const defaultTemplateString = BUSINESS_DESCRIPTION_TEMPLATE_PARTS.map((p) => (p.type === 'text' ? p.value : '...')).join('');
    const isDefaultLike = value.trim() === '' || value.trim() === defaultTemplateString.trim();
    if (!isDefaultLike) {
      setIsTemplateMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isTemplateMode) return;
    onChange(finalText);
  }, [finalText, isTemplateMode, onChange]);

  const updateTextPart = (index: number, nextValue: string) => {
    setParts((prev) => prev.map((p, i) => (i === index && p.type === 'text' ? { ...p, value: nextValue } : p)));
  };

  const updateSlot = (id: string, nextValue: string) => {
    setParts((prev) =>
      prev.map((p) => (p.type === 'slot' && p.id === id ? { ...p, value: nextValue } : p))
    );
  };

  return (
    <div dir="rtl" style={{ width: '100%', fontFamily: 'inherit', opacity: disabled ? 0.7 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 8 }}>
        <label style={{ display: 'block', fontSize: 16, color: '#000' }}>شرح کسب و کار شما</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isTemplateMode && (
            <div style={{ fontSize: 12, color: requiredMissingCount === 0 ? '#006f66' : '#8a6d00' }}>
              {filledCount}/{requiredSlotIds.length} فیلد تکمیل شده
            </div>
          )}
          <button
            type="button"
            disabled={disabled}
            onClick={() => setIsTemplateMode((v) => !v)}
            style={{
              border: '1px solid #D0D0D0',
              background: '#fff',
              borderRadius: 999,
              padding: '6px 10px',
              fontSize: 12,
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: '#444',
              transition: 'transform 120ms ease, border-color 120ms ease',
            }}
          >
            {isTemplateMode ? 'ویرایش آزاد' : 'ویرایش با قالب'}
          </button>
        </div>
      </div>

      {isTemplateMode ? (
        <div
          style={{
            minHeight: 150,
            padding: 16,
            border: error ? '1px solid #d32f2f' : '1px solid #d0d0d0',
            borderRadius: 10,
            lineHeight: 2.3,
            fontSize: 15,
            background: '#fff',
            whiteSpace: 'pre-wrap',
            boxShadow: error ? '0 0 0 3px rgba(211,47,47,0.08)' : '0 0 0 0 rgba(0,0,0,0)',
            transition: 'box-shadow 150ms ease, border-color 150ms ease',
          }}
          aria-label="business-description-template-editor"
        >
          {parts.map((part, index) => {
            if (part.type === 'text') {
              return (
                <span
                  key={`text-${index}`}
                  contentEditable={!disabled}
                  suppressContentEditableWarning
                  onBlur={(e) => updateTextPart(index, e.currentTarget.innerText)}
                  style={{ outline: 'none' }}
                >
                  {part.value}
                </span>
              );
            }

            const isActive = activeSlotId === part.id;
            const isFilled = !!part.value.trim();

            if (isActive) {
              return (
                <input
                  key={part.id}
                  autoFocus
                  disabled={disabled}
                  value={part.value}
                  placeholder={part.placeholder}
                  onChange={(e) => updateSlot(part.id, e.target.value)}
                  onBlur={() => setActiveSlotId(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setActiveSlotId(null);
                    if (e.key === 'Escape') setActiveSlotId(null);
                  }}
                  style={{
                    display: 'inline-block',
                    minWidth: 180,
                    margin: '0 4px',
                    padding: '6px 12px',
                    borderRadius: 999,
                    border: '1px solid #00aa98',
                    outline: 'none',
                    fontSize: 14,
                    direction: 'rtl',
                    background: '#fff',
                    transition: 'transform 140ms ease, box-shadow 140ms ease',
                    boxShadow: '0 6px 18px rgba(0, 170, 152, 0.12)',
                    transform: 'translateY(-1px)',
                  }}
                />
              );
            }

            return (
              <button
                key={part.id}
                type="button"
                disabled={disabled}
                onClick={() => setActiveSlotId(part.id)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  margin: '0 4px',
                  padding: '4px 12px',
                  borderRadius: 999,
                  border: isFilled ? '1px solid #00aa98' : '1px dashed #00aa98',
                  background: isFilled ? '#d9f8f3' : '#eefdfa',
                  color: isFilled ? '#006f66' : '#008f82',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  transition: 'transform 120ms ease, background 120ms ease, border-color 120ms ease',
                  transform: 'translateY(0px)',
                }}
                onMouseEnter={(e) => {
                  if (disabled) return;
                  (e.currentTarget.style.transform as any) = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget.style.transform as any) = 'translateY(0px)';
                }}
                aria-label={part.label}
              >
                {part.value || part.label}
              </button>
            );
          })}
        </div>
      ) : (
        <textarea
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '100%',
            minHeight: 160,
            resize: 'vertical',
            padding: 12,
            borderRadius: 10,
            border: error ? '1px solid #d32f2f' : '1px solid #d0d0d0',
            lineHeight: 1.9,
            fontSize: 14,
            direction: 'rtl',
            outline: 'none',
            boxShadow: error ? '0 0 0 3px rgba(211,47,47,0.08)' : 'none',
          }}
        />
      )}

      <div style={{ marginTop: 6, minHeight: 18, fontSize: 12, color: error ? '#d32f2f' : '#666' }}>
        {helperText || (isTemplateMode && requiredMissingCount > 0 ? 'برای نتیجه بهتر، چند جای خالی را تکمیل کنید.' : '')}
      </div>
    </div>
  );
}

const PersonalInfoNew: React.FC = () => {
  const navigate = useNavigate();
  const defaultBusinessDescription = "ما در ... با هدف ... فعالیت خود را آغاز کرده‌ایم و در زمینه‌ی ... خدمات ارائه می‌دهیم. تمرکز اصلی ما بر ... است و تلاش می‌کنیم با تکیه بر ...، تجربه‌ای باکیفیت و قابل اعتماد برای مشتریان خود ایجاد کنیم.\nآنچه ما را متمایز می‌کند ... است و همواره به اصولی مانند ... پایبند هستیم. هدف ما ... و ایجاد ارزشی ماندگار برای ... می‌باشد.";
  
  const [fullName, setFullName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessDescription, setBusinessDescription] = useState(defaultBusinessDescription);
  const [hasSavedBusinessDescription, setHasSavedBusinessDescription] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [errors, setErrors] = useState<{fullName?: string; businessName?: string; businessDescription?: string}>({});
  const hasFetched = useRef(false);

  useEffect(() => {
    // جلوگیری از request تکراری
    if (hasFetched.current) {
      return;
    }
    
    const fetchInitialInfo = async () => {
      try {
        hasFetched.current = true;
        setIsFetching(true);
        const response = await onboarding.getInitialInfo();
        // ممکن است response.data.data باشد یا response.data
        const data = (response.data as any)?.data || response.data;
        
        // فقط اگر null نبودند، state را به‌روزرسانی کن
        if (data?.full_name !== null && data?.full_name !== undefined) {
          setFullName(data.full_name);
        }
        
        if (data?.business_name !== null && data?.business_name !== undefined) {
          setBusinessName(data.business_name);
        }
        
        if (data?.business_description !== null && data?.business_description !== undefined) {
          const savedDescription = String(data.business_description).trim();
          if (savedDescription) {
            setBusinessDescription(savedDescription);
            setHasSavedBusinessDescription(true);
          }
        }
      } catch (error: any) {
      } finally {
        setIsFetching(false);
      }
    };

    fetchInitialInfo();
  }, []);

  const validateForm = () => {
    const newErrors: {fullName?: string; businessName?: string; businessDescription?: string} = {};
    
    if (!fullName.trim()) {
      newErrors.fullName = "لطفا نام و نام خانوادگی را وارد کنید";
    }
    
    if (!businessName.trim()) {
      newErrors.businessName = "لطفا نام کسب و کار را وارد کنید";
    }
    
    if (!businessDescription.trim()) {
      newErrors.businessDescription = "لطفا شرح کسب و کار را وارد کنید";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isSubmitting = useRef(false);

  const handleContinue = async () => {
    // جلوگیری از request تکراری
    if (isSubmitting.current || isLoading) {
      return;
    }

    // اعتبارسنجی فرم
    if (!validateForm()) {
      enqueueSnackbar('لطفا تمام فیلدها را پر کنید', { variant: 'error' });
      return;
    }

    isSubmitting.current = true;
    setIsLoading(true);
    setIsAnimating(true);

    try {
      const response = await onboarding.saveInitialInfo({
        full_name: fullName.trim(),
        business_name: businessName.trim(),
        business_description: businessDescription.trim(),
      });

      if (response.status === 200) {
        // بعد از اتمام animation به صفحه بعد برو
        setTimeout(() => {
          navigate('/onboarding/subscription');
        }, 800); // مدت زمان animation
      } else {
        setIsAnimating(false);
        setIsLoading(false);
        isSubmitting.current = false;
        enqueueSnackbar('خطا در ارسال اطلاعات. لطفا دوباره تلاش کنید.', { variant: 'error' });
      }
    } catch (error: any) {
      setIsAnimating(false);
      setIsLoading(false);
      isSubmitting.current = false;
      const errorMessage = error?.response?.data?.detail || error?.response?.data?.message || 'خطا در ارسال اطلاعات. لطفا دوباره تلاش کنید.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  return (
    <VideoProvider videoUrl={videoUrls.signup} videoTitle={videoTitles.signup}>
    <Box sx={{ width: '100%', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' ,background: 'linear-gradient(152deg,rgba(103, 240, 208, 1) 18%, rgba(255, 255, 255, 1) 100%)'}}>
        <Box sx={{ width: '100%', maxWidth: 700, display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'40px', padding: '20px'}}>
            <Box component="img" src="/images/aida-logo-ok.svg" alt="" sx={{ width: { xs: 85, sm: 100 } }} />
            <Box sx={{ width: '100%', display: 'flex', position: 'relative', gap: '20px' }}>
                {/* Stepper */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    position: 'absolute',
                    right: '-60px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '@media (max-width: 900px)': {
                        display: 'none'
                    }
                }}>
                    {/* دایره بالایی - مرحله 1 */}
                    <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#00AF9E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '18px',
                        fontWeight: 600,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        lineHeight: 1,
                        textAlign: 'center'
                    }}>
                        ۱
                    </Box>
                    {/* خط اتصال */}
                    <Box sx={{
                        width: '3px',
                        height: '60px',
                        backgroundColor: isAnimating ? '#00AF9E' : '#fff',
                        margin: '0',
                        transition: 'background-color 0.8s ease',
                    }} />
                    {/* دایره پایینی - مرحله 0 */}
                    <Box sx={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#000',
                        fontSize: '18px',
                        fontWeight: 600,
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        lineHeight: 1,
                        textAlign: 'center'
                    }}>
                        ۲
                    </Box>
                </Box>

                
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap:'40px' , backgroundColor: '#fff', padding: '20px 25px', borderRadius: '20px' }}>
                <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', gap:'30px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap:'10px' }}>
                    <p style={{ fontSize: '22px', fontWeight: 600, margin: '0px'}}>
                        سلام من آیدا هستم.👋
                    </p>
                    <p style={{ fontSize: '18px' , margin: '0px'}}>
                        وقتی شما خوابید من برای شما می‌فروشم.
                    </p>
                    </Box>
                    <p style={{ fontSize: '18px' , margin: '0px' , textAlign: 'justify'}}>
                        برای شروع و آشنایی بیشتر من از شما و کسب و کار شما اطلاعات پایین را کامل کنید.
                    </p>
                    </Box>
                <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', gap:'20px' }}>
                    <TextField
                        label="نام و نام خانوادگی"
                        placeholder="نام و نام خانوادگی"
                        fullWidth
                        value={fullName}
                        onChange={(e) => {
                            setFullName(e.target.value);
                            if (errors.fullName) {
                                setErrors({ ...errors, fullName: undefined });
                            }
                        }}
                        error={!!errors.fullName}
                        helperText={errors.fullName}
                        disabled={isFetching}
                        InputLabelProps={{
                            sx: {
                                fontSize: 16,
                                color: '#000',
                                textAlign: 'right',
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                overflow: 'hidden',
                                height: '55px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-input': {
                                    fontSize: 16,
                                    color: '#000',
                                    padding: '0 15px',
                                    backgroundColor: '#fff',
                                    height: '100%',
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                paddingLeft: '15px',
                                margin: '4px 0 0 0',
                                textAlign: 'left',
                            }
                        }}
                    />
                    <TextField
                        label="نام کسب و کار"
                        placeholder="نام کسب و کار"
                        fullWidth
                        value={businessName}
                        onChange={(e) => {
                            setBusinessName(e.target.value);
                            if (errors.businessName) {
                                setErrors({ ...errors, businessName: undefined });
                            }
                        }}
                        error={!!errors.businessName}
                        helperText={errors.businessName}
                        disabled={isFetching}
                        InputLabelProps={{
                            sx: {
                                fontSize: 16,
                                color: '#000',
                                textAlign: 'right',
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                overflow: 'hidden',
                                height: '55px',
                                borderRadius: '10px',
                                backgroundColor: '#ffffff',
                                '& .MuiInputBase-input': {
                                    fontSize: 16,
                                    color: '#000',
                                    padding: '0 15px',
                                    backgroundColor: '#fff',
                                    height: '100%',
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                paddingLeft: '15px',
                                margin: '4px 0 0 0',
                                textAlign: 'left',
                            }
                        }}
                    />
                    {!isFetching && hasSavedBusinessDescription ? (
                      <div dir="rtl" style={{ width: '100%', fontFamily: 'inherit' }}>
                        <label style={{ display: 'block', fontSize: 16, color: '#000', marginBottom: 8 }}>
                          شرح کسب و کار شما
                        </label>
                        <textarea
                          value={businessDescription}
                          onChange={(e) => {
                            setBusinessDescription(e.target.value);
                            if (errors.businessDescription) {
                              setErrors({ ...errors, businessDescription: undefined });
                            }
                          }}
                          style={{
                            width: '100%',
                            minHeight: 160,
                            resize: 'vertical',
                            padding: 12,
                            borderRadius: 10,
                            border: errors.businessDescription ? '1px solid #d32f2f' : '1px solid #d0d0d0',
                            lineHeight: 1.9,
                            fontSize: 14,
                            direction: 'rtl',
                            outline: 'none',
                            boxShadow: errors.businessDescription ? '0 0 0 3px rgba(211,47,47,0.08)' : 'none',
                          }}
                        />
                        {errors.businessDescription && (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#d32f2f' }}>
                            {errors.businessDescription}
                          </div>
                        )}
                      </div>
                    ) : !isFetching ? (
                      <BusinessDescriptionTemplateEditor
                        value={businessDescription}
                        onChange={(next) => {
                          setBusinessDescription(next);
                          if (errors.businessDescription) {
                            setErrors({ ...errors, businessDescription: undefined });
                          }
                        }}
                        error={!!errors.businessDescription}
                        helperText={errors.businessDescription}
                      />
                    ) : null}
                    <Button variant="contained" fullWidth
                    onClick={handleContinue}
                    disabled={isLoading || isFetching}
                    sx={{
                      backgroundColor:'#00AF9E',
                      color:'#fff',
                      fontSize: 20,
                      fontWeight: 600,
                      borderRadius: '10px',
                      height: '55px',
                      boxShadow:'none',
                      "&:hover": {
                        backgroundColor:'#00AF9E'
                      },
                      "&:disabled": {
                        backgroundColor: '#cccccc',
                        color: '#666666'
                      }
                    }}
                    >{isLoading ? 'در حال ارسال...' : 'ادامه'}</Button>
                </Box>
                </Box>
            </Box>
        </Box>
    </Box>
    </VideoProvider>
  );
};

export default PersonalInfoNew;
